// @ts-ignore: leaflet-image does not have type definitions
import 'leaflet.bigimage';
import 'leaflet.bigimage/dist/Leaflet.BigImage.min.css';

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, Typography, Stack, IconButton, Paper, FormControl, InputLabel, Select, OutlinedInput, MenuItem, Backdrop } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import EventsApi from '../../../api/eventsApi/eventsApi';
import PinChartModal from './pinChartModal';
import LogoSpinner from '../../../components/LogoSpinner/LogoSpinner';
import EvaluationModal from './evaluationModal';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntroductionModal from './introductionModal';
import ReportTemplate from './reportComponent/ReportTemplate';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import CoverPhotoModal from './coverPhotoModal';
import { subMonths } from 'date-fns';
import CustomDateRangePicker, { IRange } from '../../../components/dateRangePicker/CustomDateRangePicker';
import DeletionModal from './deletionModal';
import TextField from '@mui/material/TextField';
import { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';

interface WikipediaModalProps {
	open: boolean;
	onClose: () => void;
	onDelete: () => void;
	onCreateReport: () => void;
	onOpenChecklist?: () => void;
	title: string | null;
	loading: boolean;
	summary: string | null;
	savedPinData?: {
		id?: string;
		name?: string;
		latitude: number;
		longitude: number;
		radius: number;
		sector: string;
		reportDescription?: string | null;
	} | null;
}

// Normal kullanım için ikon
const customMarkerIcon = new L.Icon({
	iconUrl: '/placeholder.png',
	iconSize: [32, 32],
	iconAnchor: [16, 32],
	popupAnchor: [0, -48],
});

// Circle'ın tamamını göstermek için custom component
const FitCircleBounds: React.FC<{ lat: number; lng: number; radius: number }> = function FitCircleBounds({ lat, lng, radius }) {
	const map = useMap();
	
	useEffect(() => {
		if (!map || !lat || !lng || !radius) return;
		
		// Haritayı doğrudan merkeze ve zoom seviyesine ayarla
		map.setView([lat, lng], 11);
		
		// Ana daireyi ekle
		const mainCircle = L.circle([lat, lng], {
			radius: radius,
			color: '#ff0000',
			weight: 2,
			fillColor: 'transparent',
			fillOpacity: 0
		}).addTo(map);

		// Yarı yarıçaplı daireyi ekle
		const halfCircle = L.circle([lat, lng], {
			radius: radius / 2,
			color: '#ff0000',
			weight: 2,
			fillColor: 'transparent',
			fillOpacity: 0
		}).addTo(map);

		// Çeyrek yarıçaplı daireyi ekle
		const quarterCircle = L.circle([lat, lng], {
			radius: radius / 4,
			color: '#ff0000',
			weight: 2,
			fillColor: 'transparent',
			fillOpacity: 0
		}).addTo(map);
		
		// Cleanup
		return () => {
			map.removeLayer(mainCircle);
			map.removeLayer(halfCircle);
			map.removeLayer(quarterCircle);
		};
	}, [map, lat, lng, radius]);
	
	return null;
};

// Add type declaration for Leaflet.BigImage
declare module 'leaflet' {
	interface Control {
		bigImage(options?: {
			position?: string;
			title?: string;
			printControlLabel?: string;
			maxScale?: number;
			minScale?: number;
		}): Control;
	}
	namespace control {
		function bigImage(options?: {
			position?: string;
			title?: string;
			printControlLabel?: string;
			maxScale?: number;
			minScale?: number;
		}): Control;
	}
}

// Pin Edit Modal bileşeni
interface PinEditModalProps {
	open: boolean;
	onClose: () => void;
	pinData: {
		id?: string;
		name?: string;
		sector?: string;
		radius?: number;
		latitude?: number;
		longitude?: number;
		reportDescription?: string | null;
	} | null;
	onSave: (updatedData: any) => void;
}

const PinEditModal: React.FC<PinEditModalProps> = ({ open, onClose, pinData, onSave }) => {
	const [name, setName] = useState(pinData?.name || '');
	const [sector, setSector] = useState(pinData?.sector || '');
	const [radius, setRadius] = useState(pinData?.radius?.toString() || '');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setName(pinData?.name || '');
		setSector(pinData?.sector || '');
		setRadius(pinData?.radius?.toString() || '');
	}, [pinData]);

	const handleSubmit = async () => {
		if (!pinData?.id) return;

		setLoading(true);
		try {
			const formData = new FormData();
			formData.append('Id', pinData.id);
			formData.append('Name', name);
			formData.append('Sector', sector);
			formData.append('RadiusInMeters', Number(radius).toString());
			formData.append('UserId', localStorage.getItem('userId') || '');

			await EventsApi.updatePin(pinData.id, formData);
			onSave({ name, sector, radius: Number(radius) });
			onClose();
		} catch (error) {
			console.error('Error updating pin:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<Box sx={{ p: 3 }}>
				<Typography variant="h6" sx={{ mb: 3 }}>Pin Bilgilerini Düzenle</Typography>
				<Stack spacing={2}>
					<TextField
						fullWidth
						label="Pin Adı"
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>
					<TextField
						fullWidth
						label="Sektör"
						value={sector}
						onChange={(e) => setSector(e.target.value)}
					/>
					<TextField
						fullWidth
						label="Yarıçap (metre)"
						type="number"
						value={radius}
						onChange={(e) => setRadius(e.target.value)}
					/>
					<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
						<Button 
							onClick={onClose} 
							variant="contained"
							disabled={loading}
							sx={{ 
								backgroundColor: '#fff',
								color: '#094151',
								border: '1.5px solid #094151',
								fontWeight: 600,
								borderRadius: '8px',
								boxShadow: 'none',
								'&:hover': {
									backgroundColor: '#d0e7ec',
									color: '#094151',
								}
							}}
						>
							İptal
						</Button>
						<Button
							onClick={handleSubmit}
							variant="contained"
							disabled={loading}
							sx={{ 
								backgroundColor: '#094151',
								color: '#fff',
								fontWeight: 600,
								borderRadius: '8px',
								boxShadow: 'none',
								'&:hover': {
									backgroundColor: '#022631',
									color: '#fff',
								}
							}}
						>
							{loading ? <LogoSpinner size={24} minHeight="auto" /> : 'Kaydet'}
						</Button>
					</Box>
				</Stack>
			</Box>
		</Dialog>
	);
};

// Analysis sayfasındaki renkleri import edelim
const eventTypeColors: Record<string, string> = {
	'Terörizm (TE)': '#D76E3B',
	'Örgütlü Suçlar (OS)': '#F35E5E',
	'Sosyal ve Politik İstikrarsızlık (SP)': '#67C086',
	'Çevresel ve Doğal Afetler (CA)': '#689D7B',
	'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)': '#63ABC3',
	'Bireysel ve Adi Suçlar (BA)': '#4995DB',
	'Göç ve Mülteci Krizleri (GM)': '#8e6c58',
	'Sağlık ve Biyolojik Tehditler (SB)': '#265454',
	'Yasal ve Düzenleyici Uyumsuzluklar (YD)': '#0f223a',
	'Ekonomik ve Finansal Tehditler (EF)': '#4a3b39'
};

const WikipediaModal: React.FC<WikipediaModalProps> = ({
														   open,
														   onClose,
														   onDelete,
														   onCreateReport,
														   onOpenChecklist,
														   loading,
														   summary,
														   title,
														   savedPinData
													   }) => {
	const { id } = useParams(); // URL'den id'yi al
	const navigate = useNavigate();

	// Modal için ref oluştur
	const modalRef = useRef<HTMLDivElement>(null);
	const mapContainerRef = useRef<HTMLDivElement>(null);

	// Yeni state ekleyelim
	const [currentSummary, setCurrentSummary] = useState<string | null>(summary);
	const [isProcessing, setIsProcessing] = useState(false);
	const [animatedText, setAnimatedText] = useState('');
	const [isAnimating, setIsAnimating] = useState(false);
	const [reportData, setReportData] = useState<any>(null);
	const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
	const [introductionModalOpen, setIntroductionModalOpen] = useState(false);
	const [ReportTemplateOpen, setReportTemplateOpen] = useState(false);
	const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
	const [screenCapture, setScreenCapture] = useState<string>('');
	const [coverPhotoModalOpen, setCoverPhotoModalOpen] = useState(false);
	const [deletionModalOpen, setDeletionModalOpen] = useState(false);
	const [dateRange, setDateRange] = useState<IRange>({
		startDate: subMonths(new Date(), 1), // Default to 1 month ago
		endDate: new Date(),
	});
	const [eventsData, setEventsData] = useState<any>(null);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [pdfFiles, setPdfFiles] = useState<Array<{ id: string; fileName: string }>>([]);
	const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
	const [availableEventTypes, setAvailableEventTypes] = useState<string[]>([]);
	const [localPinData, setLocalPinData] = useState(savedPinData);

	// Get userId from localStorage
	const userId = localStorage.getItem('userId');
	const RESTRICTED_USER_ID = "f8997a6f-0c47-49bf-b1cb-2326563e16de";
	const isRestrictedUser = userId === RESTRICTED_USER_ID;

	// Debug için userId kontrolü
	useEffect(() => {
	}, [userId]);

	// summary prop'u değiştiğinde currentSummary'yi güncelle
	useEffect(() => {
		console.log('Summary or reportDescription changed:', { summary, reportDescription: savedPinData?.reportDescription });
		if (savedPinData?.reportDescription) {
			// Eğer reportDescription varsa ve null değilse onu göster
			setCurrentSummary(savedPinData.reportDescription);
		} else {
			// Eğer reportDescription null ise veya yoksa Wikipedia özetini göster
			setCurrentSummary(summary);
		}
	}, [summary, savedPinData?.reportDescription]);

	// Modal kapanırken focus'u yönet
	useEffect(() => {
		if (!open) {
			// Modal kapandığında focus'u güvenli bir şekilde yönlendir
			const mapContainer = document.querySelector('.leaflet-container');
			if (mapContainer) {
				(mapContainer as HTMLElement).focus();
			}
		} else {
			// Modal açıldığında focus'u modal içine al
			if (modalRef.current) {
				modalRef.current.focus();
			}
		}
	}, [open]);

	useEffect(() => {
		console.log('savedPinData changed:', savedPinData);
		setLocalPinData(savedPinData);
	}, [savedPinData]);

	const handleDelete = async () => {
		setDeletionModalOpen(true);
	};

	const handleOpenChecklist = () => {
		if (savedPinData?.id && onOpenChecklist) {
			onOpenChecklist();
			onClose(); // Close the Wikipedia modal when opening the checklist
		}
	};

	// Kelime kelime animasyon için yardımcı fonksiyon
	const animateText = (text: string) => {
		const words = text.split(' ');
		let currentIndex = 0;
		setIsAnimating(true);
		
		const animationInterval = setInterval(() => {
			if (currentIndex < words.length) {
				setAnimatedText(prev => prev + (prev ? ' ' : '') + words[currentIndex]);
				currentIndex++;
			} else {
				clearInterval(animationInterval);
				setIsAnimating(false);
			}
		}, 3000 / words.length); // 3 saniyeye böl

		return () => clearInterval(animationInterval);
	};

	const handleAIEdit = () => {
		setIntroductionModalOpen(true);
	};

	const handleDescriptionUpdated = () => {
		// Refresh the current summary
		if (savedPinData?.id) {
			EventsApi.getPinInfo(savedPinData.id)
				.then(pinInfo => {
					setCurrentSummary(pinInfo.ReportDescription);
				})
				.catch(error => {
					console.error('Pin bilgisi alınırken hata:', error);
				});
		}
	};

	const handleGetFullReport = async () => {
		if (!savedPinData?.id) return;
		
		try {
			const fullReport = await EventsApi.getPinFullReport(savedPinData.id);
			setReportData(fullReport);
			setReportTemplateOpen(true);
		} catch (error) {
			console.error('Rapor alınırken hata:', error);
		}
	};

	const handleScreenCapture = async () => {
		if (!mapInstance || !savedPinData) return;

		try {
			// Daire ekle
			const tempCircle = L.circle([
				savedPinData.latitude,
				savedPinData.longitude
			], {
				radius: savedPinData.radius,
				color: '#ff0000',
				weight: 2,
				fill: false
			});
			tempCircle.addTo(mapInstance);

			// BigImage kontrolünü ekle
			const bigImageControl = new (window as any).L.Control.BigImage({
				position: 'topright',
				title: 'Save Map',
				printControlLabel: '💾',
				maxScale: 2,
				minScale: 1
			});
			mapInstance.addControl(bigImageControl);

			// Override the default download functionality
			const originalDownload = bigImageControl._print;
			bigImageControl._print = function() {
				const self = this;
				const originalToBlob = self.canvas.toBlob;
				
				self.canvas.toBlob = function(blobCallback: (blob: Blob) => void) {
					originalToBlob.call(this, async (blob: Blob) => {
						// Create download link with custom filename
						const link = document.createElement('a');
						const fileName = `${savedPinData?.name || 'harita'}_harita.png`;
						link.download = fileName;
						link.href = URL.createObjectURL(blob);
						
						// Make API call before triggering download
						try {
							// Add your API call here
							// await EventsApi.saveMapImage(blob, savedPinData?.id);
						} catch (error) {
							console.error('Error saving map image:', error);
						}
						
						// Trigger download
						link.click();
						
						// Cleanup
						URL.revokeObjectURL(link.href);
						
						// Call original callback if provided
						if (blobCallback) {
							blobCallback(blob);
						}
					});
				};
				
				// Call original print function
				originalDownload.call(self);
			};

			// Otomatik scale ve download işlemi
			setTimeout(() => {
				// Scale inputunu bul ve değeri 1 yap
				const scaleInput = document.querySelector('.leaflet-bigimage-scale input') as HTMLInputElement;
				if (scaleInput) {
					scaleInput.value = '1';
					scaleInput.dispatchEvent(new Event('input', { bubbles: true }));
				}
				// Download butonuna tıkla
				const downloadBtn = document.querySelector('.leaflet-bigimage-download') as HTMLElement;
				if (downloadBtn) {
					downloadBtn.click();
				}
				// Popup'ı gizle
				const popup = document.querySelector('.leaflet-bigimage-popup') as HTMLElement;
				if (popup) {
					popup.style.display = 'none';
				}
			}, 200);

			// Cleanup
			const cleanup = () => {
				mapInstance.removeLayer(tempCircle);
				mapInstance.removeControl(bigImageControl);
			};

			// İndirme tamamlanınca cleanup
			const handleCapture = (e: any) => {
				cleanup();
				const imageData = e.imageData;
				setScreenCapture(imageData);
				mapInstance.off('bigimage:captured', handleCapture);
			};

			mapInstance.on('bigimage:captured', handleCapture);

		} catch (error) {
			console.error('Error capturing screenshot:', error);
		}
	};

	// MapComponentWrapper bileşenini ekleyelim
	const MapComponentWrapper = ({ savedPinData }: { savedPinData: any }) => {
		const map = useMap();
		
		// Map referansını ana bileşene aktaralım
		useEffect(() => {
			if (!savedPinData?.id) return;

			setMapInstance(map);
			
			// Add BigImage control using window.L
			const bigImageControl = new (window as any).L.Control.BigImage({
				position: 'topright',
				title: 'Save Map',
				printControlLabel: '💾',
				maxScale: 1,
				minScale: 1,
				inputTitle: 'Kopya Sayısı',
				downloadTitle: 'Rapora Ekle'
			});

			// Override the default download functionality
			const originalPrint = bigImageControl._print;
			bigImageControl._print = function() {
				const self = this;
				
				// Call original print function first to create canvas
				originalPrint.call(self);
				
				// Now we can safely override toBlob
				if (self.canvas && self.canvas.toBlob) {
					const originalToBlob = self.canvas.toBlob;
					self.canvas.toBlob = function(blobCallback: (blob: Blob) => void) {
						originalToBlob.call(this, async (blob: Blob) => {
							// Create FormData for updatePin
							const formData = new FormData();
							formData.append('Files', blob, `${savedPinData?.name || 'harita'}harita.png`);
							formData.append('Id', savedPinData?.id || '');
							formData.append('UserId', localStorage.getItem('userId') || '');

							// Call updatePin API
							try {
								await EventsApi.updatePin(savedPinData?.id, formData);
							} catch (error) {
								console.error('Error updating pin with map image:', error);
							}
							
							// Call original callback to trigger download
							if (blobCallback) {
								blobCallback(blob);
							}
						}, 'image/png');
					};
				}
			};

			map.addControl(bigImageControl);

			// Cleanup function
			return () => {
				map.removeControl(bigImageControl);
			};
		}, [map, savedPinData?.id]);
		
		return (
			<>
				<FitCircleBounds 
					lat={savedPinData.latitude} 
					lng={savedPinData.longitude} 
					radius={savedPinData.radius} 
				/>
				<TileLayer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					attribution=""
				/>
				<Marker
					position={[savedPinData.latitude, savedPinData.longitude]}
					icon={customMarkerIcon}
				/>
				<Circle
					center={[savedPinData.latitude, savedPinData.longitude]}
					radius={savedPinData.radius}
					pathOptions={{
						color: '#ff0000',
						fillColor: 'transparent',
						weight: 2,
						opacity: 1,
						fillOpacity: 0
					}}
				/>
				<Circle
					center={[savedPinData.latitude, savedPinData.longitude]}
					radius={savedPinData.radius / 2}
					pathOptions={{
						color: '#ff0000',
						fillColor: 'transparent',
						weight: 2,
						opacity: 1,
						fillOpacity: 0
					}}
				/>
				<Circle
					center={[savedPinData.latitude, savedPinData.longitude]}
					radius={savedPinData.radius / 4}
					pathOptions={{
						color: '#ff0000',
						fillColor: 'transparent',
						weight: 2,
						opacity: 1,
						fillOpacity: 0
					}}
				/>
			</>
		);
	};

	// Fetch events data when component mounts or pin changes
	useEffect(() => {
		const fetchEventsData = async () => {
			if (!savedPinData?.id) return;

			try {
				const formattedStartDate = dateRange.startDate.toISOString().split('T')[0];
				const formattedEndDate = dateRange.endDate.toISOString().split('T')[0];
				
				const response = await EventsApi.getPinEvents(
					savedPinData.id,
					formattedStartDate,
					formattedEndDate
				);
				
				// Mevcut olay tiplerini topla
				const allEvents = response.locations
					.filter((location: any) => location.district1 !== "Unknown District")
					.flatMap((location: any) => location.events);
				
				const eventTypes = Array.from(new Set(allEvents.map((event: any) => event.eventType as string)));
				setAvailableEventTypes(eventTypes);
				
				setEventsData(response);
			} catch (error) {
				console.error('Error fetching events data:', error);
			}
		};

		fetchEventsData();
	}, [savedPinData?.id, dateRange]);

	const handleEventTypeChange = (event: SelectChangeEvent<string[]>) => {
		const value = event.target.value;
		setSelectedEventTypes(
			typeof value === 'string' ? value.split(',') : value,
		);
	};

	const handlePinUpdate = (updatedData: any) => {
		if (savedPinData) {
			const updatedPinData = {
				...savedPinData,
				name: updatedData.name,
				sector: updatedData.sector,
				radius: updatedData.radius
			};
			setLocalPinData(updatedPinData);
		}
	};

	// PDF dosyalarını yükle
	useEffect(() => {
		const loadPdfFiles = async () => {
			if (!savedPinData?.id) return;
			
			try {
				const pinInfo = await EventsApi.getPinInfo(savedPinData.id);
				const pdfAttachments = pinInfo.fileAttachments?.filter(
					(file: { fileName: string }) => file.fileName.toLowerCase().endsWith('.pdf')
				) || [];
				setPdfFiles(pdfAttachments);
			} catch (error) {
				console.error('PDF dosyaları yüklenirken hata:', error);
			}
		};

		loadPdfFiles();
	}, [savedPinData?.id]);

	const handlePdfDownload = async (fileId: string, fileName: string) => {
		try {
			const blob = await EventsApi.getFileAttachment(fileId);
			
			// Create a download link
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName;
			
			// Trigger download
			document.body.appendChild(link);
			link.click();
			
			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('PDF indirme hatası:', error);
		}
	};

	// URL'den geliyorsa (standalone sayfa) normal göster, modal modundaysa gizle
	const isStandalonePage = !!id;
	
	// Modal modunda ve kapalıysa hiçbir şey render etme
	if (!isStandalonePage && !open) {
		return null;
	}

	return (
		<Box sx={{ 
			p: { xs: 2, sm: 3, md: 4 }, 
			maxWidth: '1200px', 
			margin: '0 auto',
			minHeight: '100vh',
			bgcolor: 'background.default',
			// Modal modunda yüksek z-index ve backdrop ekle
			...(isStandalonePage ? {} : {
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 9999,
				bgcolor: 'rgba(0, 0, 0, 0.8)',
				overflow: 'auto',
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'center',
				pt: 2
			})
		}}>
			{/* Modal modunda kapatma butonu */}
			{!isStandalonePage && (
				<IconButton 
					onClick={onClose}
					sx={{ 
						position: 'absolute',
						right: 16,
						top: 16,
						color: 'white',
						bgcolor: 'rgba(0, 0, 0, 0.5)',
						'&:hover': {
							bgcolor: 'rgba(0, 0, 0, 0.7)'
						}
					}}
				>
					<CloseIcon />
				</IconButton>
			)}
			
			{/* Standalone modunda geri dönüş butonu */}
			{isStandalonePage && (
				<IconButton 
					onClick={() => navigate(-1)}
					sx={{ mb: 2 }}
				>
					<ArrowBackIcon />
				</IconButton>
			)}

			{/* Ana İçerik Container */}
			<Box sx={{ 
				mb: 4,
				// Modal modunda içerik styling
				...(isStandalonePage ? {} : {
					width: '95%',
					maxWidth: '1200px',
					bgcolor: 'background.default',
					borderRadius: 2,
					position: 'relative',
					maxHeight: '90vh',
					overflow: 'auto'
				})
			}}>
				{/* Pin Bilgileri Bölümü */}
				<Box sx={{
					background: '#fff',
					borderRadius: '12px',
					boxShadow: 2,
					p: 3,
					mb: 3
				}}>
					<Box 
						sx={{ 
							display: 'flex', 
							alignItems: 'center', 
							cursor: 'pointer',
							mb: 2,
							'&:hover .edit-icon': {
								opacity: 1
							}
						}}
						onClick={() => setEditModalOpen(true)}
					>
						<Typography variant="h5" sx={{ fontWeight: 700 }}>
							{localPinData?.name || title || 'Pin Adı'}
						</Typography>
						<EditIcon 
							className="edit-icon" 
							sx={{ 
								ml: 1, 
								opacity: 0, 
								transition: 'opacity 0.2s',
								color: 'text.secondary',
								fontSize: '1.2rem'
							}} 
						/>
					</Box>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
						<strong>Sektör:</strong> {localPinData?.sector || '-'}
					</Typography>
					<Typography variant="body1" color="text.secondary">
						<strong>Yarıçap:</strong> {localPinData?.radius ? `${localPinData.radius} metre` : '-'}
					</Typography>
				</Box>

				{/* Harita Bölümü */}
				<Box sx={{
					background: '#fff',
					borderRadius: '12px',
					boxShadow: 2,
					p: 3,
					mb: 3
				}}>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
						Harita Görünümü
					</Typography>
					<Box 
						ref={mapContainerRef}
						sx={{
							height: '400px',
							width: '100%',
							borderRadius: '8px',
							overflow: 'hidden',
							position: 'relative'
						}}
					>
						{localPinData && (
							<MapContainer
								center={[localPinData.latitude, localPinData.longitude]}
								zoom={14}
								style={{ 
									height: '100%', 
									width: '100%'
								}}
								dragging={true}
								touchZoom={true}
								doubleClickZoom={true}
								scrollWheelZoom={true}
								boxZoom={false}
								keyboard={false}
								zoomControl={true}
							>
								<MapComponentWrapper savedPinData={localPinData} />
							</MapContainer>
						)}
					</Box>
				</Box>

				{/* Aksiyon Butonları */}
				<Box sx={{
					background: '#fff',
					borderRadius: '12px',
					boxShadow: 2,
					p: 3,
					mb: 3
				}}>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
						İşlemler
					</Typography>
					<Box sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
						gap: 2
					}}>
						<Button
							variant="contained"
							startIcon={<EditIcon />}
							onClick={handleAIEdit}
							disabled={isProcessing || isAnimating}
							sx={{
								backgroundColor: '#fff',
								color: '#094151',
								border: '1.5px solid #094151',
								fontWeight: 600,
								borderRadius: '8px',
								boxShadow: 'none',
								py: 1.5,
								'&:hover': {
									backgroundColor: '#d0e7ec',
									color: '#094151',
								},
							}}
						>
							Genel Bakış Ekle
						</Button>
						<Button
							variant="contained"
							startIcon={<CameraAltIcon />}
							onClick={() => setCoverPhotoModalOpen(true)}
							sx={{
								backgroundColor: '#fff',
								color: '#094151',
								border: '1.5px solid #094151',
								fontWeight: 600,
								borderRadius: '8px',
								boxShadow: 'none',
								py: 1.5,
								'&:hover': {
									backgroundColor: '#d0e7ec',
									color: '#094151',
								},
							}}
						>
							Kapak Fotoğrafı Ekle
						</Button>
						{!isRestrictedUser && (
							<Button
								variant="contained"
								startIcon={<ListAltIcon />}
								onClick={handleOpenChecklist}
								sx={{
									backgroundColor: '#fff',
									color: '#094151',
									border: '1.5px solid #094151',
									fontWeight: 600,
									borderRadius: '8px',
									boxShadow: 'none',
									py: 1.5,
									'&:hover': {
										backgroundColor: '#d0e7ec',
										color: '#094151',
									},
								}}
							>
								Kontrol Listesine Git
							</Button>
						)}
						{!isRestrictedUser && (
							<Button
								variant="contained"
								startIcon={<EditIcon />}
								onClick={() => setEvaluationModalOpen(true)}
								sx={{
									backgroundColor: '#fff',
									color: '#094151',
									border: '1.5px solid #094151',
									fontWeight: 600,
									borderRadius: '8px',
									boxShadow: 'none',
									py: 1.5,
									'&:hover': {
										backgroundColor: '#d0e7ec',
										color: '#094151',
									},
								}}
							>
								Sonuç ve Değerlendirme Ekle
							</Button>
						)}
						{!isRestrictedUser && (
							<Button
								variant="contained"
								onClick={handleGetFullReport}
								sx={{
									backgroundColor: '#094151',
									color: '#fff',
									fontWeight: 600,
									borderRadius: '8px',
									boxShadow: 'none',
									py: 1.5,
									'&:hover': {
										backgroundColor: '#022631',
										color: '#fff',
									},
								}}
							>
								Rapor Taslağı
							</Button>
						)}
						<Button
							variant="contained"
							onClick={handleDelete}
							sx={{
								backgroundColor: '#d32f2f',
								color: '#fff',
								fontWeight: 600,
								borderRadius: '8px',
								boxShadow: 'none',
								py: 1.5,
								'&:hover': {
									backgroundColor: '#b71c1c',
									color: '#fff',
								},
							}}
						>
							Sil
						</Button>
					</Box>
				</Box>

				{/* Kaydedilen Raporlar Bölümü */}
				<Box sx={{
					background: '#fff',
					borderRadius: '12px',
					boxShadow: 2,
					p: 3,
					mb: 3
				}}>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
						Kaydedilen Raporlar
					</Typography>
					{pdfFiles.length === 0 ? (
						<Box sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							py: 4,
							color: 'text.secondary',
							border: '2px dashed',
							borderColor: 'divider',
							borderRadius: 2
						}}>
							<Typography variant="body1">
								Henüz kaydedilmiş rapor bulunmamaktadır.
							</Typography>
						</Box>
					) : (
						<Box sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
							gap: 2
						}}>
							{pdfFiles.map((file) => (
								<Paper
									key={file.id}
									elevation={1}
									sx={{
										p: 2,
										borderRadius: 2,
										display: 'flex',
										alignItems: 'center',
										gap: 2,
										cursor: 'pointer',
										transition: 'all 0.2s',
										'&:hover': {
											bgcolor: 'action.hover',
											boxShadow: 3,
											'& .download-icon': {
												opacity: 1
											}
										}
									}}
									onClick={() => handlePdfDownload(file.id, file.fileName)}
								>
									<PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: '2rem' }} />
									<Box sx={{ flex: 1, overflow: 'hidden' }}>
										<Typography variant="body2" noWrap>
											{file.fileName}
										</Typography>
									</Box>
									<DownloadIcon 
										className="download-icon"
										sx={{ 
											color: 'action.active',
											opacity: 0.5,
											transition: 'opacity 0.2s'
										}} 
									/>
								</Paper>
							))}
						</Box>
					)}
				</Box>

				{/* Olay İstatistikleri Bölümü */}
				<Box sx={{ 
					background: '#fff',
					borderRadius: '12px',
					boxShadow: 2,
					p: 3,
					mb: 3
				}}>
					<Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
						Olay İstatistikleri
					</Typography>
					
					{/* Filtreler */}
					<Box sx={{ 
						display: 'flex',
						flexDirection: { xs: 'column', md: 'row' },
						gap: 2,
						mb: 3,
						alignItems: { xs: 'stretch', md: 'center' }
					}}>
						<FormControl size="small" sx={{ minWidth: { xs: '100%', md: 200 } }}>
							<InputLabel id="event-type-select-label">Olay Türü</InputLabel>
							<Select
								labelId="event-type-select-label"
								multiple
								value={selectedEventTypes}
								onChange={handleEventTypeChange}
								input={<OutlinedInput label="Olay Türü" />}
								renderValue={(selected) => {
									if ((selected as string[]).length === 0) return 'Olay Türü';
									if ((selected as string[]).length === availableEventTypes.length) return 'Tümü seçili';
									if ((selected as string[]).length === 1) return '1 seçili';
									if ((selected as string[]).length === 2) return '2 seçili';
									return `${(selected as string[]).length} seçili`;
								}}
							>
								{availableEventTypes.map((eventType) => (
									<MenuItem key={eventType} value={eventType}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Box
												sx={{
													width: 12,
													height: 12,
													backgroundColor: eventTypeColors[eventType] || '#999',
													borderRadius: '50%',
												}}
											/>
											{eventType}
										</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ width: { xs: '100%', md: 300 } }}>
							<CustomDateRangePicker
								dateRange={dateRange}
								setDateRange={setDateRange}
								label="Tarih Aralığı"
							/>
						</Box>
					</Box>

					{/* Seçili olay türleri chip olarak gösterilsin */}
					{selectedEventTypes.length > 0 && (
						<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
							{selectedEventTypes.map((eventType) => (
								<Chip
									key={eventType}
									label={eventType}
									onDelete={() => {
										setSelectedEventTypes(selectedEventTypes.filter((et) => et !== eventType));
									}}
									deleteIcon={<CloseIcon />}
									size="small"
									sx={{
										backgroundColor: eventTypeColors[eventType] || '#999',
										color: '#fff',
										fontWeight: 500,
										fontSize: '0.85em',
										height: 28,
									}}
									variant="filled"
								/>
							))}
						</Box>
					)}

					<PinChartModal 
						pinId={localPinData?.id} 
						eventsData={eventsData}
						startDate={dateRange.startDate}
						endDate={dateRange.endDate}
						selectedEventTypes={selectedEventTypes}
					/>
				</Box>
			</Box>

			<EvaluationModal
				open={evaluationModalOpen}
				onClose={() => setEvaluationModalOpen(false)}
				pinId={localPinData?.id || ''}
				userId={userId || ''}
			/>

			<IntroductionModal
				open={introductionModalOpen}
				onClose={() => setIntroductionModalOpen(false)}
				pinId={localPinData?.id || ''}
				currentDescription={currentSummary}
				onDescriptionUpdated={handleDescriptionUpdated}
				userId={userId || ''}
				startDate={dateRange.startDate}
				endDate={dateRange.endDate}
			/>

			<ReportTemplate
				open={ReportTemplateOpen}
				onClose={() => setReportTemplateOpen(false)}
				reportData={reportData}
			/>

			<CoverPhotoModal
				open={coverPhotoModalOpen}
				onClose={() => setCoverPhotoModalOpen(false)}
				pinId={localPinData?.id || ''}
				userId={userId || ''}
				onPhotoUploaded={() => {
					// You can add any refresh logic here if needed
					setCoverPhotoModalOpen(false);
				}}
			/>

			<DeletionModal
				open={deletionModalOpen}
				onClose={() => setDeletionModalOpen(false)}
				pinId={localPinData?.id || ''}
			/>

			<PinEditModal
				open={editModalOpen}
				onClose={() => setEditModalOpen(false)}
				pinData={localPinData || null}
				onSave={handlePinUpdate}
			/>
		</Box>
	);
};

export default WikipediaModal;